/** Sales report PDF — print window with charts + non-overlapping product table */
import { resolveBranchForPrint } from './branchPrint';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (n) =>
  `LKR ${Number(n || 0).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const num = (v) => {
  const n = parseFloat(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const pick = (row, key) => row?.[key] ?? row?.dataValues?.[key];

/**
 * @param {object} data - sales report API payload (data object)
 * @param {object} options - { startDate, endDate, branchName, branchCode }
 */
export const exportSalesReportPdf = (data = {}, options = {}) => {
  const branch = resolveBranchForPrint(options);
  const summary = data.summary || {};
  const insights = data.analysis?.insights || [];
  const trends = Array.isArray(data.salesTrends) ? [...data.salesTrends] : [];
  const payments = Array.isArray(data.paymentBreakdown) ? data.paymentBreakdown : [];
  const products = Array.isArray(data.topProducts) ? data.topProducts : [];

  // Oldest → newest for chart
  const trendRows = trends
    .map((t) => ({
      period: String(pick(t, 'period') || ''),
      sales: num(pick(t, 'total_sales')),
      bills: num(pick(t, 'transaction_count'))
    }))
    .sort((a, b) => a.period.localeCompare(b.period))
    .slice(-15);

  const maxTrend = Math.max(1, ...trendRows.map((t) => t.sales));
  const trendWithDir = trendRows.map((t, i, arr) => {
    const prev = i > 0 ? arr[i - 1].sales : null;
    let dir = 'flat';
    let pct = 0;
    if (prev != null && prev > 0) {
      pct = ((t.sales - prev) / prev) * 100;
      dir = pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat';
    } else if (prev === 0 && t.sales > 0) {
      dir = 'up';
      pct = 100;
    } else if (prev > 0 && t.sales === 0) {
      dir = 'down';
      pct = -100;
    }
    return { ...t, dir, pct };
  });

  const trendBars = trendWithDir
    .map((t) => {
      const h = Math.max(4, (t.sales / maxTrend) * 140);
      const color = t.dir === 'up' ? '#2e7d32' : t.dir === 'down' ? '#c62828' : '#1976d2';
      const arrow = t.dir === 'up' ? '▲' : t.dir === 'down' ? '▼' : '–';
      const label = t.period.slice(-5);
      return `
        <div class="t-col">
          <div class="t-arrow" style="color:${color}">${arrow}</div>
          <div class="t-bar-wrap">
            <div class="t-bar" style="height:${h}px;background:${color}" title="${esc(money(t.sales))}"></div>
          </div>
          <div class="t-label">${esc(label)}</div>
          <div class="t-pct" style="color:${color}">${t.dir === 'flat' ? '' : `${arrow}${Math.abs(t.pct).toFixed(0)}%`}</div>
        </div>`;
    })
    .join('');

  const payRows = payments.map((p) => ({
    method: String(pick(p, 'payment_method') || 'N/A'),
    count: num(pick(p, 'transaction_count')),
    amount: num(pick(p, 'total_amount'))
  }));
  const payTotal = Math.max(1, payRows.reduce((s, p) => s + p.amount, 0));
  const payColors = ['#1976d2', '#2e7d32', '#f57c00', '#7b1fa2', '#00838f', '#c62828'];

  const payBars = payRows
    .map((p, i) => {
      const pct = (p.amount / payTotal) * 100;
      return `
        <div class="pay-row">
          <div class="pay-head">
            <strong>${esc(p.method.toUpperCase())}</strong>
            <span>${esc(money(p.amount))} · ${p.count} bills · ${pct.toFixed(1)}%</span>
          </div>
          <div class="track"><div class="fill" style="width:${pct}%;background:${payColors[i % payColors.length]}"></div></div>
        </div>`;
    })
    .join('');

  const productRows = products
    .slice(0, 10)
    .map((p, i) => {
      const name = pick(p, 'product_name') || 'Product';
      const qty = num(pick(p, 'total_quantity'));
      const sales = num(pick(p, 'total_sales'));
      return `<tr>
        <td class="center">${i + 1}</td>
        <td class="prod-name">${esc(name)}</td>
        <td class="right">${qty}</td>
        <td class="right">${esc(money(sales))}</td>
      </tr>`;
    })
    .join('');

  const insightHtml = insights
    .slice(0, 6)
    .map(
      (ins) =>
        `<div class="insight"><strong>${esc(ins.title)}</strong><div>${esc(ins.detail || '')}</div></div>`
    )
    .join('');

  const periodLabel = `${options.startDate || ''} to ${options.endDate || ''}`;
  const win = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes');
  if (!win) {
    alert('Please allow pop-ups for PDF export');
    return;
  }

  const baseUrl = window.location.origin;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Sales Report</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
  *{box-sizing:border-box}
  body{font-family:Poppins,Arial,sans-serif;font-size:11px;color:#222;margin:0;padding:18px 22px}
  .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:12px}
  .logo{height:64px}
  .company{text-align:right}
  .company-name{font-weight:700;color:#1976d2;font-size:13px}
  .banner{display:flex;justify-content:space-between;gap:12px;background:linear-gradient(90deg,#e3f2fd,#f8fbff);border:1px solid #bbdefb;border-radius:8px;padding:12px 14px;margin-bottom:14px}
  .title{font-size:16px;font-weight:700;color:#1565c0;margin:0 0 4px}
  .badge{background:#1976d2;color:#fff;padding:8px 12px;border-radius:20px;font-weight:600;white-space:nowrap;align-self:flex-start}
  .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:14px}
  .kpi{border:1px solid #e0e0e0;border-radius:8px;padding:10px;background:#fafafa}
  .kpi .l{font-size:9px;color:#666;text-transform:uppercase}
  .kpi .v{font-size:13px;font-weight:700;margin-top:3px;word-break:break-word}
  .section{margin:16px 0 8px;font-size:13px;font-weight:700;color:#1565c0;border-bottom:1px solid #ddd;padding-bottom:4px}
  .insights{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px}
  .insight{background:#f5f9fc;border:1px solid #e3f2fd;border-radius:6px;padding:8px 10px}
  .insight strong{display:block;margin-bottom:3px;color:#1565c0}
  .chart-box{border:1px solid #e0e0e0;border-radius:8px;padding:12px;margin-bottom:12px;background:#fff}
  .trend{display:flex;align-items:flex-end;justify-content:space-around;gap:4px;min-height:200px;padding-top:8px}
  .t-col{flex:1;min-width:0;max-width:48px;display:flex;flex-direction:column;align-items:center}
  .t-arrow{font-size:12px;font-weight:700;height:16px;line-height:16px}
  .t-bar-wrap{height:140px;display:flex;align-items:flex-end;width:100%;justify-content:center}
  .t-bar{width:70%;max-width:28px;border-radius:5px 5px 0 0;min-height:4px}
  .t-label{font-size:9px;color:#666;margin-top:4px;white-space:nowrap}
  .t-pct{font-size:8px;font-weight:700;margin-top:2px;min-height:10px}
  .pay-row{margin-bottom:10px}
  .pay-head{display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;flex-wrap:wrap}
  .track{background:#eceff1;height:12px;border-radius:6px;overflow:hidden}
  .fill{height:100%;border-radius:6px}
  table{width:100%;border-collapse:collapse;margin-top:6px;table-layout:fixed}
  th{background:#1976d2;color:#fff;text-align:left;padding:8px;font-size:10px}
  td{border-bottom:1px solid #eee;padding:8px;vertical-align:top;font-size:10px}
  tr:nth-child(even) td{background:#f8fbff}
  .prod-name{word-wrap:break-word;overflow-wrap:anywhere;white-space:normal;line-height:1.35}
  .right{text-align:right}
  .center{text-align:center;width:36px}
  col.rank{width:36px} col.qty{width:70px} col.sales{width:130px}
  .footer{margin-top:16px;text-align:center;color:#777;font-size:10px;border-top:1px dashed #ccc;padding-top:10px}
  .actions{margin-top:16px}
  .actions button{background:#1976d2;color:#fff;border:none;border-radius:4px;padding:8px 14px;font-weight:600;margin-right:8px;cursor:pointer}
  @media print{.no-print{display:none!important} body{padding:8mm} .insights{grid-template-columns:1fr}}
  @media (max-width:700px){.kpis{grid-template-columns:1fr 1fr}.insights{grid-template-columns:1fr}}
</style></head><body>
  <div class="header">
    <img class="logo" src="${baseUrl}/LOGOFile.png" onerror="this.style.display='none'" alt="Logo"/>
    <div class="company">
      <div class="company-name">Sun System ERP</div>
      <div>${esc(branch.label)}</div>
      ${branch.code ? `<div>Code: ${esc(branch.code)}</div>` : ''}
    </div>
  </div>

  <div class="banner">
    <div>
      <h1 class="title">SALES REPORT</h1>
      <div>Period: ${esc(periodLabel)}</div>
      <div>Generated: ${esc(new Date().toLocaleString('en-GB'))}</div>
    </div>
    <div class="badge">${esc(branch.label)}</div>
  </div>

  <div class="kpis">
    <div class="kpi"><div class="l">Net Sales</div><div class="v">${esc(money(summary.netSales))}</div></div>
    <div class="kpi"><div class="l">Transactions</div><div class="v">${esc(summary.totalTransactions || 0)}</div></div>
    <div class="kpi"><div class="l">Avg Sale</div><div class="v">${esc(money(summary.averageTransaction))}</div></div>
    <div class="kpi"><div class="l">Cash Collected</div><div class="v">${esc(money(summary.collectedSales))}</div></div>
  </div>

  ${insightHtml ? `<div class="section">Auto analysis</div><div class="insights">${insightHtml}</div>` : ''}

  <div class="section">Sales trend chart (▲ up / ▼ down)</div>
  <div class="chart-box">
    <div class="trend">${trendBars || '<div>No trend data</div>'}</div>
  </div>

  <div class="section">Payment methods chart</div>
  <div class="chart-box">
    ${payBars || '<div>No payment data</div>'}
  </div>

  <div class="section">Payment summary</div>
  <div class="kpis">
    <div class="kpi"><div class="l">Cash</div><div class="v">${esc(money(summary.cashSales))}</div></div>
    <div class="kpi"><div class="l">Card</div><div class="v">${esc(money(summary.cardSales))}</div></div>
    <div class="kpi"><div class="l">Cheque</div><div class="v">${esc(money(summary.chequeSales))}</div></div>
    <div class="kpi"><div class="l">Online / Bank</div><div class="v">${esc(money(summary.onlineSales))}</div></div>
  </div>

  <div class="section">Top selling products</div>
  <table>
    <colgroup>
      <col class="rank"/>
      <col/>
      <col class="qty"/>
      <col class="sales"/>
    </colgroup>
    <thead>
      <tr>
        <th class="center">#</th>
        <th>Product</th>
        <th class="right">Qty</th>
        <th class="right">Sales</th>
      </tr>
    </thead>
    <tbody>
      ${productRows || '<tr><td colspan="4">No products in this period</td></tr>'}
    </tbody>
  </table>

  <div class="footer">Sun System ERP · Confidential · ${esc(branch.label)}</div>
  <div class="no-print actions">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button onclick="window.close()" style="background:#607d8b">Close</button>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print()},450)}</script>
</body></html>`);
  win.document.close();
};
